import React, { useEffect, useState, useCallback } from "react"
import { connect } from "react-redux"
import { Row, Col, Card, CardBody, Input, Badge, Progress } from "reactstrap"
import { Link } from "react-router-dom"
import "./Dashboard.scss"

// Pages Components
import Miniwidget from "./Miniwidget"
import QuickStats from "./QuickStats"
import RecentUsers from "./RecentUsers"
import PendingTasks from "./PendingTasks"
import DashboardSummary from "./DashboardSummary"
import ContentModerationPanel from "./ContentModerationPanel"
import BusinessInsights from "./BusinessInsights"
import SecurityAlerts from "./SecurityAlerts"
import UserEngagementMetrics from "./UserEngagementMetrics"
import AdminActionCenter from "./AdminActionCenter"
import AreaChart from "../AllCharts/apex/areachart"

//Import Action to copy breadcrumb items from local state to redux state
import { setBreadcrumbItems } from "../../store/actions"
import CommonApi from "Api/CommonApi"

const Dashboard = props => {
  document.title = "Dashboard | Pixwik - Admin"
  const accessToken = localStorage.getItem("accessToken")

  const breadcrumbItems = [
    { title: "Pixwik", link: "#" },
    { title: "Dashboard", link: "#" },
  ]

  useEffect(() => {
    props.setBreadcrumbItems("Dashboard", breadcrumbItems)
  })

  const [metadata, setMetaData] = useState({})
  const [filterStatus, setFilterStatus] = useState("all")

  // Dummy data for enhanced dashboard - replace with real API calls later
  const [dashboardData] = useState({
    totalUsers: 15420,
    activeUsers: 12350,
    newUsersToday: 45,
    totalBusinessUsers: 1250,
    pendingKYC: 23,
    totalTransactions: 8950,
    totalRevenue: 125000,
    emailsSent: 2340,
    categoriesCount: 15,
    institutionsCount: 89,
    recentActivity: [
      { user: "John Doe", action: "Registered", time: "2 mins ago", type: "success" },
      { user: "Jane Smith", action: "KYC Submitted", time: "5 mins ago", type: "info" },
      { user: "Mike Johnson", action: "Payment Failed", time: "10 mins ago", type: "danger" },
      { user: "Sarah Wilson", action: "Profile Updated", time: "15 mins ago", type: "warning" },
      { user: "Tom Brown", action: "Business Verified", time: "20 mins ago", type: "success" }
    ],
    topCategories: [
      { name: "Technology", posts: 1250, percentage: 25 },
      { name: "Education", posts: 980, percentage: 20 },
      { name: "Business", posts: 750, percentage: 15 },
      { name: "Health", posts: 620, percentage: 12 },
      { name: "Entertainment", posts: 400, percentage: 8 }
    ],
    userGrowth: {
      thisMonth: 1250,
      lastMonth: 980,
      growth: 27.5
    },
    systemHealth: {
      serverUptime: 99.9,
      responseTime: 120,
      errorRate: 0.1
    }
  })

  const getAllMetaData = useCallback(() => {
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }

    CommonApi.getUserTypeCount(config)
      .then(response => {
        setMetaData(response.data.data)
      })
      .catch(error => {
        console.error("Error fetching metadata:", error)
      })
  }, [accessToken])

  useEffect(() => {
    getAllMetaData()
  }, [getAllMetaData])

  const reports = [
    {
      title: "Student",
      iconClass: " ion-ios-school",
      key: "studentCount",
    },
    {
      title: "Employee",
      iconClass: " ion-md-business",
      key: "employeeCount",
    },
    {
      title: "Creator",
      iconClass: " ion-md-list-box",
      key: "creatorCount",
    },
  ]

  const updatedReports = reports.map(report => ({
    ...report,
    total: metadata[report.key] || report.total,
  }))

  return (
    <div className="dashboard-page">
      {/* Dashboard Summary - Full Width Header */}
      <div className="mb-3">
        <DashboardSummary data={dashboardData} />
      </div>

      {/* Original Mini Widgets - Preserved */}
      <div className="mb-3">
        <Miniwidget reports={updatedReports} />
      </div>

      {/* Quick Stats Row */}
      <div className="mb-3">
        <QuickStats data={dashboardData} />
      </div>

      {/* Main Analytics & System Health Row */}
      <Row className="mb-3">
        <Col lg="8" className="mb-3 mb-lg-0">
          <Card className="h-100 border-0 shadow-sm">
            <CardBody>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h4 className="card-title mb-0">User Analytics</h4>
                <Input
                  type="select"
                  value={filterStatus}
                  onChange={e => setFilterStatus(e.target.value)}
                  style={{ width: "150px" }}
                >
                  <option value="all">All Users</option>
                  <option value="students">Students</option>
                  <option value="business">Business</option>
                  <option value="creators">Creators</option>
                </Input>
              </div>

              <Row className="text-center mb-3 g-2">
                <Col sm="3">
                  <div className="p-2 bg-light rounded">
                    <h5 className="mb-0 font-size-18 text-primary">{dashboardData.totalUsers.toLocaleString()}</h5>
                    <p className="text-muted mb-0 font-size-12">Total Users</p>
                  </div>
                </Col>
                <Col sm="3">
                  <div className="p-2 bg-light rounded">
                    <h5 className="mb-0 font-size-18 text-success">{dashboardData.activeUsers.toLocaleString()}</h5>
                    <p className="text-muted mb-0 font-size-12">Active Users</p>
                  </div>
                </Col>
                <Col sm="3">
                  <div className="p-2 bg-light rounded">
                    <h5 className="mb-0 font-size-18 text-info">{dashboardData.newUsersToday}</h5>
                    <p className="text-muted mb-0 font-size-12">New Today</p>
                  </div>
                </Col>
                <Col sm="3">
                  <div className="p-2 bg-light rounded">
                    <h5 className="mb-0 font-size-18 text-warning">{dashboardData.userGrowth.growth}%</h5>
                    <p className="text-muted mb-0 font-size-12">Growth Rate</p>
                  </div>
                </Col>
              </Row>

              <div dir="ltr" style={{ height: '250px' }}>
                <AreaChart />
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col lg="4">
          <Card className="h-100 border-0 shadow-sm">
            <CardBody>
              <h4 className="card-title mb-3">System Health</h4>

              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span className="font-size-13">Server Uptime</span>
                  <span className="text-success font-weight-bold font-size-13">{dashboardData.systemHealth.serverUptime}%</span>
                </div>
                <Progress value={dashboardData.systemHealth.serverUptime} color="success" className="progress-sm" />
              </div>

              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span className="font-size-13">Response Time</span>
                  <span className="text-info font-weight-bold font-size-13">{dashboardData.systemHealth.responseTime}ms</span>
                </div>
                <Progress value={75} color="info" className="progress-sm" />
              </div>

              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span className="font-size-13">Error Rate</span>
                  <span className="text-warning font-weight-bold font-size-13">{dashboardData.systemHealth.errorRate}%</span>
                </div>
                <Progress value={dashboardData.systemHealth.errorRate * 10} color="warning" className="progress-sm" />
              </div>

              <div className="text-center mt-3">
                <Badge color="success" className="badge-soft">
                  <i className="mdi mdi-check-circle me-1"></i>
                  All Systems Operational
                </Badge>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Business Insights & Security Row */}
      <Row className="mb-3">
        <Col lg="8" className="mb-3 mb-lg-0">
          <BusinessInsights />
        </Col>
        <Col lg="4">
          <SecurityAlerts />
        </Col>
      </Row>

      {/* Content Moderation & Admin Actions Row */}
      <Row className="mb-3">
        <Col lg="8" className="mb-3 mb-lg-0">
          <ContentModerationPanel />
        </Col>
        <Col lg="4">
          <AdminActionCenter />
        </Col>
      </Row>

      {/* User Engagement & Recent Activity Row */}
      <Row className="mb-3">
        <Col lg="6" className="mb-3 mb-lg-0">
          <UserEngagementMetrics />
        </Col>
        <Col lg="6">
          <Card className="h-100 border-0 shadow-sm">
            <CardBody>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h4 className="card-title mb-0">Recent Activity</h4>
                <Link to="/user-list" className="btn btn-sm btn-outline-primary">
                  <i className="mdi mdi-eye me-1"></i>
                  View All
                </Link>
              </div>

              <div className="activity-feed" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {dashboardData.recentActivity.map((activity, index) => (
                  <div key={index} className="d-flex align-items-center mb-2 p-2 rounded border"
                    style={{ borderLeft: `3px solid ${activity.type === 'success' ? '#28a745' : activity.type === 'danger' ? '#dc3545' : '#17a2b8'}` }}>
                    <div className={`avatar-sm me-2`}>
                      <span className={`avatar-title rounded-circle bg-${activity.type} text-white`}>
                        <i className={`mdi ${activity.type === 'success' ? 'mdi-check' :
                          activity.type === 'info' ? 'mdi-information' :
                            activity.type === 'danger' ? 'mdi-close' :
                              'mdi-alert'
                          }`}></i>
                      </span>
                    </div>
                    <div className="flex-1">
                      <h6 className="mb-0 font-size-13">{activity.user}</h6>
                      <p className="text-muted mb-0 font-size-12">{activity.action}</p>
                      <small className="text-muted font-size-11">{activity.time}</small>
                    </div>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Recent Users & Pending Tasks Row */}
      <Row className="mb-3">
        <Col lg="8" className="mb-3 mb-lg-0">
          <RecentUsers />
        </Col>
        <Col lg="4">
          <PendingTasks />
        </Col>
      </Row>

      {/* Action Cards Row */}
      <Row className="mb-3">
        <Col lg="4" className="mb-3 mb-lg-0">
          <Card className="h-100 border-0 shadow-sm">
            <CardBody>
              <h4 className="card-title mb-3">Quick Actions</h4>

              <div className="d-grid gap-2">
                <Link to="/PortalUsers" className="btn btn-outline-primary btn-sm d-flex align-items-center justify-content-start">
                  <i className="mdi mdi-account-plus me-2"></i>
                  Add Portal User
                </Link>
                <Link to="/categoryList" className="btn btn-outline-success btn-sm d-flex align-items-center justify-content-start">
                  <i className="mdi mdi-tag-plus me-2"></i>
                  Add Category
                </Link>
                <Link to="/institutions" className="btn btn-outline-info btn-sm d-flex align-items-center justify-content-start">
                  <i className="mdi mdi-school me-2"></i>
                  Manage Institutions
                </Link>
                <Link to="/kyc" className="btn btn-outline-warning btn-sm d-flex align-items-center justify-content-start">
                  <i className="mdi mdi-file-document me-2"></i>
                  Review KYC
                </Link>
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col lg="4" className="mb-3 mb-lg-0">
          <Card className="h-100 border-0 shadow-sm">
            <CardBody>
              <h4 className="card-title mb-3">Payment Overview</h4>

              <div className="text-center mb-3 p-2 bg-light rounded">
                <h3 className="text-primary mb-0">${dashboardData.totalRevenue.toLocaleString()}</h3>
                <p className="text-muted mb-0 font-size-12">Total Revenue</p>
              </div>

              <Row className="text-center mb-3 g-2">
                <Col sm="6">
                  <div className="p-2 border rounded">
                    <h6 className="mb-0 font-size-14 text-info">{dashboardData.totalTransactions.toLocaleString()}</h6>
                    <p className="text-muted mb-0 font-size-11">Transactions</p>
                  </div>
                </Col>
                <Col sm="6">
                  <div className="p-2 border rounded">
                    <h6 className="mb-0 font-size-14 text-success">$142</h6>
                    <p className="text-muted mb-0 font-size-11">Avg. Value</p>
                  </div>
                </Col>
              </Row>

              <Link to="/transactions" className="btn btn-primary btn-sm w-100">
                <i className="mdi mdi-credit-card me-1"></i>
                View Transactions
              </Link>
            </CardBody>
          </Card>
        </Col>

        <Col lg="4">
          <Card className="h-100 border-0 shadow-sm">
            <CardBody>
              <h4 className="card-title mb-3">Communication</h4>

              <div className="d-flex align-items-center justify-content-between mb-2 p-2 bg-light rounded">
                <div>
                  <h6 className="mb-0 font-size-14">Emails Sent</h6>
                  <p className="text-muted mb-0 font-size-11">This month</p>
                </div>
                <h5 className="text-info mb-0">{dashboardData.emailsSent.toLocaleString()}</h5>
              </div>

              <div className="d-flex align-items-center justify-content-between mb-3 p-2 bg-light rounded">
                <div>
                  <h6 className="mb-0 font-size-14">Email Templates</h6>
                  <p className="text-muted mb-0 font-size-11">Active templates</p>
                </div>
                <h5 className="text-success mb-0">12</h5>
              </div>

              <Link to="/EmailTemplateTable" className="btn btn-outline-primary btn-sm w-100">
                <i className="mdi mdi-email me-1"></i>
                Manage Templates
              </Link>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Categories Overview - Compact */}
      <Row>
        <Col lg="12">
          <Card className="border-0 shadow-sm">
            <CardBody className="pb-3">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h4 className="card-title mb-0">Top Categories</h4>
                <Link to="/categoryList" className="btn btn-sm btn-outline-primary">
                  <i className="mdi mdi-cog me-1"></i>
                  Manage
                </Link>
              </div>

              <Row className="g-2">
                {dashboardData.topCategories.map((category, index) => (
                  <Col lg="2" md="4" sm="6" key={index}>
                    <div className="text-center p-2 border rounded category-card">
                      <div className={`avatar-sm rounded-circle bg-primary d-flex align-items-center justify-content-center mx-auto mb-2`}>
                        <span className="text-white font-size-12">{index + 1}</span>
                      </div>
                      <h6 className="mb-1 font-size-13">{category.name}</h6>
                      <p className="text-muted mb-1 font-size-11">{category.posts} posts</p>
                      <Badge color="success" className="badge-soft font-size-10">
                        {category.percentage}%
                      </Badge>
                    </div>
                  </Col>
                ))}
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  )
}

export default connect(null, { setBreadcrumbItems })(Dashboard)